import { computed, ref } from 'vue'

import type { ComputedRef, WritableComputedRef } from 'vue'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'

interface FilterDateRangeOptions {
  initialFrom: string | null
  initialTo: string | null
}

interface FilterDateRangeReturn {
  range: WritableComputedRef<Date[] | null>
  from: ComputedRef<string | null>
  to: ComputedRef<string | null>
}

const parseDate = (value: string | null): Date | null => {
  if (!value) return null
  const parsed = dayjs(value, DateFormat.Default)
  return parsed.isValid() ? parsed.toDate() : null
}

const buildInitialRange = (initialFrom: string | null, initialTo: string | null): Date[] | null => {
  const from = parseDate(initialFrom)
  const to = parseDate(initialTo)

  if (!from || !to) return null

  return [from, to]
}

export const useFilterDateRange = ({ initialFrom, initialTo }: FilterDateRangeOptions): FilterDateRangeReturn => {
  const internalRange = ref<Date[] | null>(buildInitialRange(initialFrom, initialTo))

  const range = computed<Date[] | null>({
    get: () => internalRange.value,
    set: (value) => {
      internalRange.value = (Array.isArray(value) && value.length === 2) ? value : null
    },
  })

  const from = computed<string | null>(() => {
    const value = internalRange.value?.[0]
    if (!value) return null
    return dayjs(value).format(DateFormat.Default)
  })

  const to = computed<string | null>(() => {
    const value = internalRange.value?.[1]
    if (!value) return null
    return dayjs(value).format(DateFormat.Default)
  })

  return {
    range,
    from,
    to,
  }
}
